import { formatSellerType } from '../../../../../../helpers/formatting';
import type { InfoTitleStarRatingProps } from './InfoTitleStarRating.typed';
import * as Styled from './InfoTitleStarRating.styled';
import { theme } from '../../../../../../helpers/Theme';
import { scrollElementIntoView } from '../../../../../../helpers/scroll';
import { Text } from '../../../../../ToolkitV2/Text/Text';
import { FEATURE_TDP_UPDATES } from '../../../../../../utils';
import { Fragment } from 'react';

function handleRatingClick(clickTargetElementId: string) {
  const element = document.getElementById(`${clickTargetElementId}`);

  if (element) {
    scrollElementIntoView({ element });
  }
}

const getMainDealerSubTitle = (
  isTrustedDealer: boolean,
  franchiseType?: string,
) => {
  if (FEATURE_TDP_UPDATES && isTrustedDealer) {
    return (
      franchiseType && (
        <Fragment key="dealer-star-rating">
          <Styled.TrustedBadge
            width="16"
            height="16"
            color={theme.colors.GREEN}
            data-testid="trusted-dealer-icon"
          />
          Trusted {franchiseType}
        </Fragment>
      )
    );
  }
  return franchiseType;
};

function InfoTitleStarRating({
  rating,
  displayReviews,
  maxRating = 5,
  clickTargetElementId = 'google-dealer-reviews',
  isDealer,
  position = 'MAIN',
  franchiseType,
  county,
  isTrader,
  verification,
  type,
  ratingGroupDataTracking,
  variant = 'LARGE',
  anchorToRatings = true,
  onClickSubtitle,
  isTrustedDealer = false,
}: InfoTitleStarRatingProps) {
  const RATING_FONT_SIZE = variant === 'LARGE' ? 'M14' : 'B12';

  return (
    <Styled.Wrapper>
      {/* This logic is from the listItem component in
      `components/Toolkit/MiniUserProfile/MiniUserProfile.tsx`. This allows us to create one component with both the desired text and rating component. */}
      <Styled.SubTitleLinkButton
        ofType="TERTIARY"
        fontSize="M14"
        fontWeight="regular"
        onClick={onClickSubtitle}
      >
        {isDealer
          ? position === 'MAIN'
            ? [getMainDealerSubTitle(isTrustedDealer, franchiseType)]
            : [county]
          : [county, formatSellerType(isTrader, verification, type)]}{' '}
      </Styled.SubTitleLinkButton>
      {displayReviews && rating && (
        <Styled.RatingContainer variant={variant}>
          <Styled.SStarIc
            color={theme.colors.ORANGE}
            width="16"
            height="16"
            variant={variant}
            data-testid="star-icon"
          />
          <Styled.RatingGroup>
            <Styled.RatingText anchorToRatings={anchorToRatings}>
              <Text
                size={RATING_FONT_SIZE}
                color={'GREY_DARKER'}
                weight="semibold"
                forwardedAs="span"
              >
                {rating}
              </Text>
              <Text
                size={RATING_FONT_SIZE}
                color={'GREY'}
                weight="semibold"
                forwardedAs="span"
              >
                /{maxRating}
              </Text>
            </Styled.RatingText>

            {anchorToRatings && (
              <Styled.RatingAnchor
                ofType="SECONDARY"
                fontSize={RATING_FONT_SIZE}
                fontWeight="semibold"
                onClick={() => handleRatingClick(clickTargetElementId)}
                inline
                data-tracking={ratingGroupDataTracking}
              >
                <Text size={RATING_FONT_SIZE} color="BLUE" weight="semibold">
                  {rating}/{maxRating}
                </Text>
              </Styled.RatingAnchor>
            )}
          </Styled.RatingGroup>
        </Styled.RatingContainer>
      )}
    </Styled.Wrapper>
  );
}

export { InfoTitleStarRating };
